import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Textarea } from '@/components/ui/textarea';
import { usePayments } from '@/hooks/use-payments';
import { useAuth } from '@/hooks/use-auth';
import { toast } from 'sonner';

interface BulkCreatePaymentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BulkCreatePaymentsDialog({ open, onOpenChange }: BulkCreatePaymentsDialogProps) {
  const { addPaymentsBulk } = usePayments();
  const { user } = useAuth();
  const [amount, setAmount] = useState('50');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('Monthly membership fee');
  const [names, setNames] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const list = names
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(Boolean);
    if (!amount || !dueDate || list.length === 0) {
      toast.error('Please provide amount, due date, and at least one member name');
      return;
    }
    const items = list.map((memberName, idx) => ({
      memberId: `member-${Date.now()}-${idx}`,
      memberName,
      amount: parseFloat(amount),
      currency: 'USD',
      paymentDate: '',
      dueDate,
      status: 'pending' as const,
      paymentMethod: '',
      description,
      adminId: user?.id,
    }));
    try {
      const created = await addPaymentsBulk(items);
      toast.success(`Created ${created.length} payment${created.length === 1 ? '' : 's'}`);
      onOpenChange(false);
      setNames('');
    } catch (err) {
      console.error(err);
      toast.error('Failed to create payments');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Bulk Create Membership Dues</DialogTitle>
          <DialogDescription>
            Create pending dues for multiple members at once.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField
            label="Amount"
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          <FormField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            required
          />
          <FormField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="space-y-2">
            <label className="text-sm font-medium">Member names (one per line)</label>
            <Textarea
              placeholder="Jane Doe\nJohn Smith\n..."
              rows={6}
              value={names}
              onChange={(e) => setNames(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Create Dues</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
